// TANDA AS-3 (FIRMA #27) — LOS ERRORES DE APPLE, EN EL IDIOMA DEL USUARIO.
//
// Hermano de `lib/insufficientCreditsCopy.js` y con el mismo oficio: el backend rechaza
// con un CÓDIGO estructurado (`{code, message}`) y aquí ese código se convierte en la clave
// i18n que la superficie pinta con su mecanismo de error de siempre (toast o estado). El
// `message` en inglés que arma el servidor NUNCA se pinta.
//
// LA REGLA DEL EDGE (lección medida en producción el 2026-08-11, AS-2.2): Cloudflare
// SUSTITUYE los 502/504 del origen por su propia página HTML. O sea que el frontend puede
// recibir un 502 SIN nuestro JSON. El mapeo, en orden:
//
//   1. el cuerpo trae nuestro `code`            → su fila;
//   2. el estatus es 502/504 y el cuerpo NO es
//      nuestro JSON                             → `apple_verification_unavailable`
//                                                 (es exactamente el mismo consejo: es
//                                                 transitorio y el pago está a salvo);
//   3. cualquier otra cosa ilegible             → el genérico.
//
// El backend NO se toca por esto: la transacción se queda pendiente en la cola del cartero
// (lib/purchases.js) y "Restaurar compras" es la segunda red.

const PREFIJO = 'pricing:iap.errors.';

/** Los diez códigos de /payments/apple/verify → su clave i18n. */
export const APPLE_ERROR_KEYS = {
  guest_cannot_purchase: `${PREFIJO}guest_cannot_purchase`,
  subscription_conflict_stripe: `${PREFIJO}subscription_conflict_stripe`,
  subscription_conflict_apple: `${PREFIJO}subscription_conflict_apple`,
  apple_transaction_owned_by_other_account: `${PREFIJO}apple_transaction_owned_by_other_account`,
  apple_unknown_product: `${PREFIJO}apple_unknown_product`,
  apple_invalid_transaction: `${PREFIJO}apple_invalid_transaction`,
  apple_bundle_mismatch: `${PREFIJO}apple_bundle_mismatch`,
  apple_missing_transaction: `${PREFIJO}apple_missing_transaction`,
  apple_verification_unavailable: `${PREFIJO}apple_verification_unavailable`,
  apple_billing_not_configured: `${PREFIJO}apple_billing_not_configured`,
};

export const APPLE_ERROR_GENERIC_KEY = `${PREFIJO}generic`;

// Rechazos DEFINITIVOS: reintentar no los arregla, así que el recibo sale de la cola de
// pendientes (y se finaliza con StoreKit).
//
// LA REGLA (AS-3.2, la corrección firmada). El reparto tiene TRES grupos, no dos:
//
//   · DEFINITIVO — los SIETE de abajo. El backend ya dijo su última palabra sobre ESE
//     recibo (producto que no vendemos, recibo de otra cuenta, firma que no cuadra,
//     conflicto de suscripción): reintentarlo mil veces daría mil veces lo mismo.
//
//   · ESPERA DE LOGIN — `guest_cannot_purchase` (403). NO está en este conjunto, y esa
//     ausencia es la decisión. El 403 no habla del recibo: habla de QUIÉN está preguntando
//     en este momento. El mismo recibo, con una cuenta real en la sesión, se cobra sin
//     problema — así que el recibo PERMANECE en la cola y NO se finaliza con StoreKit,
//     esperando el siguiente drenaje.
//
//     POR QUÉ NO SE PUEDE DESCARTAR AQUÍ (y por qué "Restaurar compras" no bastaría como
//     red): un CONSUMIBLE finalizado desaparece del historial de StoreKit, y Restaurar no
//     lo devuelve. Descartar un recibo pagado en este punto sería perder créditos
//     comprados sin remedio, y el usuario no tendría manera de recuperarlos.
//
//   · TRANSITORIO — los 5xx (`apple_verification_unavailable` 502,
//     `apple_billing_not_configured` 503), el 502/504 del edge de Cloudflare sin nuestro
//     JSON, y cualquier caída de red. Mismo trato que la espera de login: el recibo se
//     queda y se reintenta.
const DEFINITIVOS = new Set([
  'subscription_conflict_stripe',
  'subscription_conflict_apple',
  'apple_transaction_owned_by_other_account',
  'apple_unknown_product',
  'apple_invalid_transaction',
  'apple_bundle_mismatch',
  'apple_missing_transaction',
]);

/**
 * El código de error de Apple que corresponde a este rechazo, o `null` si no se puede
 * saber. Aplica la regla del edge.
 *
 * @param err el error de axios (o cualquier cosa: nunca revienta)
 */
export function appleErrorCode(err) {
  const res = err?.response;
  const detail = res?.data?.detail;
  const code = (detail && typeof detail === 'object') ? detail.code : null;
  if (code && APPLE_ERROR_KEYS[code]) return code;
  const status = res?.status;
  if (status === 502 || status === 504) return 'apple_verification_unavailable';
  return null;
}

/** ¿Este rechazo es definitivo (no vale la pena reintentarlo nunca)? */
export function appleErrorIsDefinitive(err) {
  const res = err?.response;
  const detail = res?.data?.detail;
  const code = (detail && typeof detail === 'object') ? detail.code : null;
  return !!code && DEFINITIVOS.has(code);
}

/**
 * El copy traducido de un rechazo de /payments/apple/verify.
 *
 * @param err el error de axios
 * @param t   la `t` de la superficie que llama (necesita el namespace `pricing`)
 * @returns {string} SIEMPRE una frase: un código desconocido cae en el genérico.
 */
export function appleErrorCopy(err, t) {
  const code = appleErrorCode(err);
  return t(code ? APPLE_ERROR_KEYS[code] : APPLE_ERROR_GENERIC_KEY);
}

export default appleErrorCopy;
